/**
 * Memecraft 2026 palette. Hue-shifted ramps (shadows lean cool/violet, lights lean warm) so
 * volumes read painterly instead of flat. Colours are RGBA tuples for the software renderer.
 */
export type Col = readonly [number, number, number, number];

export function hex(h: string, a = 255): Col {
  const n = parseInt(h.replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, a];
}

export function withA(c: Col, a: number): Col {
  return [c[0], c[1], c[2], Math.max(0, Math.min(255, Math.round(a)))];
}

export function mix(a: Col, b: Col, t: number): Col {
  const u = Math.max(0, Math.min(1, t));
  return [
    Math.round(a[0] + (b[0] - a[0]) * u),
    Math.round(a[1] + (b[1] - a[1]) * u),
    Math.round(a[2] + (b[2] - a[2]) * u),
    Math.round(a[3] + (b[3] - a[3]) * u),
  ];
}

export function shade(c: Col, k: number): Col {
  return [Math.round(c[0] * k), Math.round(c[1] * k), Math.round(c[2] * k), c[3]];
}

/** Interface colours: deep navy glass, cream text, gold focus, teal accents. */
export const UI = {
  ink: hex("#0e1020"),
  bg0: hex("#141a2e"),
  bg1: hex("#1d2544"),
  bg2: hex("#283259"),
  bg3: hex("#36427a"),
  line: hex("#4c5a9c"),
  lineHi: hex("#8e9de0"),
  text: hex("#f6f1e6"),
  textDim: hex("#a9b0d4"),
  textMute: hex("#6f78a8"),
  gold: hex("#ffc84a"),
  goldHi: hex("#fff0a8"),
  goldDk: hex("#c9862a"),
  teal: hex("#58e0cf"),
  tealDk: hex("#2a8f9a"),
  pink: hex("#ff6f91"),
  red: hex("#ff5a4f"),
  green: hex("#5de07a"),
  greenDk: hex("#2e9a52"),
  yellow: hex("#ffd84a"),
  yellowDk: hex("#c99a1c"),
  redDk: hex("#b3303f"),
  paper: hex("#fbf4e4"),
  paperDk: hex("#e6d7b8"),
  paperInk: hex("#2b2238"),
  shadow: hex("#07080f", 110),
  white: hex("#ffffff"),
  black: hex("#000000"),
} as const;

/** Type chips: bright fill + deep rim so they read on both dark and light panels. */
export const TYPE_COL: Record<string, { fill: Col; rim: Col; hi: Col }> = {
  cope: { fill: hex("#e8537a"), rim: hex("#8f2447"), hi: hex("#ff9ab4") },
  flex: { fill: hex("#f2a93b"), rim: hex("#9c5a14"), hi: hex("#ffd98a") },
  chaos: { fill: hex("#9b5cf2"), rim: hex("#4f2596"), hi: hex("#c9a3ff") },
  signal: { fill: hex("#35c6e6"), rim: hex("#1a6b8f"), hi: hex("#9aeeff") },
  liquid: { fill: hex("#3d7ff0"), rim: hex("#1f3f96"), hi: hex("#98bcff") },
  viral: { fill: hex("#4fcf62"), rim: hex("#237a35"), hi: hex("#a8f59f") },
  burn: { fill: hex("#ff7a3d"), rim: hex("#a8321c"), hi: hex("#ffc28a") },
  hodl: { fill: hex("#8fd8f2"), rim: hex("#2f6f9c"), hi: hex("#e4f8ff") },
  rug: { fill: hex("#7a4fa0"), rim: hex("#34204e"), hi: hex("#c5a2e6") },
  ai: { fill: hex("#a9b6cc"), rim: hex("#46526a"), hi: hex("#eef3fb") },
  moon: { fill: hex("#f5d86a"), rim: hex("#8a6414"), hi: hex("#fff5c4") },
  stake: { fill: hex("#6cc35a"), rim: hex("#2c6a34"), hi: hex("#c4f2a4") },
  rekt: { fill: hex("#8a7cb0"), rim: hex("#3a3056"), hi: hex("#d4ccee") },
};

/** World material ramps, light → dark. */
export const W = {
  grass: [hex("#c9ef7c"), hex("#96d35d"), hex("#6fb84e"), hex("#4f9a45"), hex("#397a3d"), hex("#285a36"), hex("#1b3d2c")],
  meadow: [hex("#dff48a"), hex("#b3e06a"), hex("#86c653")],
  dirt: [hex("#f0d6a0"), hex("#dcb97e"), hex("#c49a63"), hex("#a67a4d"), hex("#80583a"), hex("#5a3c2c")],
  cobble: [hex("#f1e6cf"), hex("#d8c7a6"), hex("#b9a584"), hex("#8f7c66"), hex("#63554c"), hex("#3f3740")],
  plaza: [hex("#efe2c4"), hex("#dccaa4"), hex("#c4ae88"), hex("#9f8a6c"), hex("#6f6052")],
  sand: [hex("#f7e9bd"), hex("#ead59b"), hex("#d6ba7c"), hex("#b8975f")],
  water: [hex("#e9fbff"), hex("#9fe3f5"), hex("#5cc0e8"), hex("#3a93d6"), hex("#2a6bb8"), hex("#214c93"), hex("#1a3468")],
  leaf: [hex("#b8e36a"), hex("#86c957"), hex("#5fab4b"), hex("#43893f"), hex("#2f6936"), hex("#214b2f"), hex("#16302a")],
  pine: [hex("#7fbf6a"), hex("#579f59"), hex("#3c7e4d"), hex("#2b5f42"), hex("#1e4236"), hex("#142a28")],
  bark: [hex("#b07a4f"), hex("#8a5a3a"), hex("#673f2c"), hex("#462a22")],
  rock: [hex("#e4e0d8"), hex("#bdb8b4"), hex("#94909a"), hex("#6d6a7c"), hex("#4b4a5e"), hex("#302f40")],
  cliff: [hex("#e8c9a0"), hex("#c9a077"), hex("#a57a5a"), hex("#7c5747"), hex("#553b39"), hex("#35252c")],
  roofRed: [hex("#ff9d7a"), hex("#e86a5a"), hex("#c24850"), hex("#8f2f45"), hex("#5e1f3a")],
  roofBlue: [hex("#9fd0ff"), hex("#6aa2ec"), hex("#4677c9"), hex("#324f99"), hex("#22336a")],
  roofTeal: [hex("#9ff0d6"), hex("#5fcfb4"), hex("#3a9f94"), hex("#276f74"), hex("#1b4a55")],
  wall: [hex("#fff6e2"), hex("#f1e1c0"), hex("#dac39c"), hex("#b59c7c"), hex("#86705e")],
  wood: [hex("#e6b17a"), hex("#c98a55"), hex("#a3663e"), hex("#7a4830"), hex("#523026")],
  flowers: [hex("#ff7fa3"), hex("#ffd35c"), hex("#ffffff"), hex("#9a8cff"), hex("#ff9950"), hex("#7fd8ff")],
  lamp: hex("#ffd88a"),
  glow: hex("#ffb45a"),
  shadow: hex("#1a1633"),
} as const;
